import type { UserOut } from "@/types/api";
import client from "./client";
import { resizeImage, toJpegBlob } from "@/utils/routePhoto";

export async function getMe(): Promise<UserOut> {
  const res = await client.get<UserOut>("/users/me");
  return res.data;
}

export async function updateMe(data: {
  handle?: string;
  bio?: string;
  birthdate?: string;
  climbing_since?: number | null;
  instagram_url?: string | null;
  strava_url?: string | null;
  youtube_url?: string | null;
  tiktok_url?: string | null;
  email_notifications_follow?: boolean;
}): Promise<UserOut> {
  const res = await client.patch<UserOut>("/users/me", data);
  return res.data;
}

/**
 * Upload a new profile photo. Every caller goes through here, so the photo is
 * always re-encoded first: that drops its EXIF data, such as where it was
 * taken, before it leaves the device (the privacy policy relies on this).
 */
export async function uploadPhoto(file: Blob): Promise<UserOut> {
  const reencoded = await resizeImage(await toJpegBlob(file), 800);
  const form = new FormData();
  form.append("file", new File([reencoded], "profile.jpg", { type: "image/jpeg" }));
  const res = await client.post<UserOut>("/users/me/photo", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

export async function deleteMe(): Promise<void> {
  await client.delete("/users/me");
}

export async function changePassword(data: {
  current_password: string;
  new_password: string;
}): Promise<{ message: string }> {
  const res = await client.post<{ message: string }>("/users/me/change-password", data);
  return res.data;
}

export async function changeEmail(data: {
  new_email: string;
  current_password?: string;
}): Promise<{ message: string; pending_email: string }> {
  const res = await client.post<{ message: string; pending_email: string }>(
    "/users/me/change-email",
    data
  );
  return res.data;
}

export async function cancelEmailChange(): Promise<UserOut> {
  const res = await client.post<UserOut>("/users/me/change-email/cancel");
  return res.data;
}

export async function exportMe(): Promise<void> {
  const res = await client.get("/users/me/export", { responseType: "blob" });
  const url = URL.createObjectURL(res.data as Blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "sendstats-export.json";
  a.click();
  URL.revokeObjectURL(url);
}
